import { useQuery } from "@tanstack/react-query";
import { GetProviderName, GPUInstanceType } from "components/Environment/Settings/Tabs/Compute/InstanceChanger/GPUTypes";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/Graphs/Select";
import { InputField } from "components/UI-lib";
import React, { useEffect, useState } from "react";
import agent from "server";

const GpuHourCalculator: React.FC = () => {
  const [basePrice, setBasePrice] = useState<number>(0);
  const [credits, setCredits] = useState<number>(0);
  const [gpuHours, setGpuHours] = useState<string>("1");
  const [isElasticStorage, setIsElasticStorage] = useState<boolean>(false);

  const { data: instanceTypes } = useQuery({
    queryFn: () => getInstanceTypes(),
    queryKey: ["instance-types"],
  });

  useEffect(() => {
    if (gpuHours) {
      setCredits(parseFloat(gpuHours) * basePrice);
    } else {
      setCredits(0);
    }
  }, [basePrice, gpuHours]);

  const getInstanceTypes = async () => {
    const res = await agent.Instances.getAllPublicInstanceTypes();
    if (!res.success || !res.data) {
      throw new Error("Failed to fetch instance types");
    }
    const instanceTypes: Record<string, GPUInstanceType> = {};
    res.data?.allInstanceTypes
      .filter((item) => item.base_price?.amount)
      .forEach((item) => {
        instanceTypes[item.type] = item;
      });
    return instanceTypes;
  };

  const onGpuHoursChange = (value: string) => {
    const hours = parseFloat(value);
    if (hours >= 0 || !value) {
      setGpuHours(value);
    } else {
      setGpuHours("0");
    }
  };

  return (
    <div className="flex flex-col">
      <h2 className="text-xl font-bold mb-4 text-white">Credit Calculator</h2>
      <div className="flex flex-row gap-6 items-start">
        <div className="flex flex-col gap-1 w-1/3">
          <label className="text-sm font-medium text-gray-700 dark:text-secondary">
            1. Select instance Type
          </label>
          <Select
            onValueChange={(value) => {
              setBasePrice(Number(instanceTypes?.[value]?.base_price!.amount));
              setIsElasticStorage(
                instanceTypes?.[value]?.elastic_root_volume ?? false
              );
            }}
          >
            <SelectTrigger
              id="size"
              className="h-[38px] dark:border-borderPrimary"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(instanceTypes || {}).map(
                ([key, instanceType]) => (
                  <SelectItem key={key} value={key}>
                    {key} ({GetProviderName(instanceType)})
                  </SelectItem>
                )
              )}
            </SelectContent>
          </Select>
        </div>
        <InputField
          label="2. Enter GPU hours"
          type="number"
          value={gpuHours}
          errorMessage=""
          onChange={onGpuHoursChange}
        />
        <div className="text-3xl self-center mt-5 text-gray-700 dark:text-secondary">
          ≈
        </div>
        <div className="flex flex-col gap-2 text-white">
          <div className="text-sm font-medium text-gray-700 dark:text-secondary">
            Credits (USD)
            {isElasticStorage && "*"}
          </div>
          <div>
            {credits.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            })}
          </div>
        </div>
      </div>
      {basePrice > 0 && (
        <div className="text-xs text-gray-700 dark:text-secondary mt-1">
          Base Price: ${basePrice.toFixed(2)}
        </div>
      )}
      {isElasticStorage && (
        <div className="text-xs text-gray-700 dark:text-secondary">
          *Actual cost may differ for instances with flexible storage
        </div>
      )}
    </div>
  );
};

export default GpuHourCalculator;
